import { flexRender } from "@tanstack/react-table";
import { Pagination } from "antd";
import { useTable } from "hooks";
import React, { useEffect, useState } from "react";
import { UseTableProps } from "types/tables";

const CustomTable = <T,>({
  columns,
  initialData,
  rowBackgroundColor = "bg-white",
  pagination,
  total,
  addRowStyle,
  isPlaceholderData,
  handleTableChange = () => {},
  showPagination = true,
}: UseTableProps<T>) => {
  const { table, data, setData } = useTable({
    columns,
    initialData,
  });
  const [isMobile, setIsMobile] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Update table data when initialData changes
  useEffect(() => {
    setData(initialData);
  }, [initialData, setData]);

  // Check screen size on mount and when window resizes
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Handle page size changes
  const handleSizeChange = (_current: number, size: number) => {
    handleTableChange(1, size);
  };

  // Toggle row expanded state for mobile view
  const toggleRowExpand = (index: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Get the first N columns to display in collapsed mobile view
  const getPrimaryColumns = () => {
    // Display first 2 columns in collapsed view
    return table.getHeaderGroups()[0].headers.slice(0, 2);
  };

  // Function to render mobile-optimized table
  const renderMobileTable = () => {
    if (!data || data.length === 0) {
      return (
        <div className="text-center py-6 text-[#8C8C8C] border rounded-lg">
          No Data Available
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {table.getRowModel().rows.map((row, rowIndex) => {
          const isExpanded = expandedRows.has(rowIndex);

          return (
            <div
              key={row.id}
              className={`rounded-lg border shadow-sm overflow-hidden ${rowBackgroundColor}`}
              style={{ ...addRowStyle?.(row.original) }}
            >
              {/* Always visible row summary */}
              <div
                className="flex items-center justify-between p-4 cursor-pointer"
                onClick={() => toggleRowExpand(rowIndex)}
              >
                <div className="flex-1">
                  {/* Display first column as title */}
                  {getPrimaryColumns().map((header) => {
                    const cell = row
                      .getVisibleCells()
                      .find((cell) => cell.column.id === header.column.id);

                    if (!cell) return null;

                    return (
                      <div key={header.id} className="mb-1 last:mb-0">
                        <div className="font-medium">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="text-blue-500 ml-2">
                  {isExpanded ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="18 15 12 9 6 15"></polyline>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  )}
                </div>
              </div>

              {/* Expandable details */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-0 border-t">
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    // Skip the first two columns as they're already shown above
                    if (cellIndex < 2) return null;

                    const header = table
                      .getHeaderGroups()[0]
                      .headers.find((h) => h.column.id === cell.column.id);

                    if (!header) return null;

                    // For action column
                    if (cell.column.id === "actions") {
                      return (
                        <div
                          key={cell.id}
                          className="mt-4 pt-2 border-t flex justify-end"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      );
                    }

                    return (
                      <div
                        key={cell.id}
                        className="py-2 grid grid-cols-2 gap-2"
                      >
                        <div className="text-[#8C8C8C] font-medium">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          :
                        </div>
                        <div className="text-[#1F2734]">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Function to render desktop table
  const renderDesktopTable = () => {
    return (
      <div className="w-full border rounded-lg overflow-x-auto">
        <table className="border-spacing-y-table w-full table-auto">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-[#EAECF0]">
                {headerGroup.headers.map((header, index) => (
                  <th
                    key={header.id}
                    className={`text-[#8C8C8C] font-semibold text-[0.85rem] py-4 px-4 text-left 
                    ${index === 0 ? "rounded-tl-lg" : ""} 
                    ${
                      index === headerGroup.headers.length - 1
                        ? "rounded-tr-lg"
                        : ""
                    }`}
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      backgroundColor: "#EAECF0",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody className={rowBackgroundColor}>
            {data && data.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="text-[#1F2734] text-[0.85rem] hover:bg-gray-50"
                  style={{ ...addRowStyle?.(row.original) }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={`py-4 px-4 ${
                        cell.column.id === "actions"
                          ? "sticky right-0 bg-white"
                          : ""
                      }`}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={table.getHeaderGroups()[0].headers.length}>
                  <div className="text-center py-6 text-[#8C8C8C]">
                    No Data Available
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="w-full flex flex-col gap-4">
      {isMobile ? renderMobileTable() : renderDesktopTable()}

      {showPagination && (
        <div className="flex justify-center sm:justify-end">
          <Pagination
            current={pagination?.current}
            pageSize={pagination?.pageSize}
            total={total}
            showSizeChanger
            onChange={handleTableChange}
            onShowSizeChange={handleSizeChange}
            size={isMobile ? "small" : "default"}
            className="text-sm"
          />
        </div>
      )}
    </div>
  );
};

export default CustomTable;
